/**
 * Geezer Bundle Cart
 * ──────────────────
 * Picker za bundle PDP. Podržava DVA režima:
 *
 *   - 'slots'  — FBT-style horizontal picker; svaki slot ima default proizvod
 *                + alternative koje user može da bira preko "Zameni proizvod"
 *                dropdown-a. Variant dropdowns se re-renderuju kad se proizvod
 *                u slotu promeni.
 *
 *   - 'legacy' — Fiksni proizvodi (R4 backward compat); user bira samo
 *                varijantu (Color/Size) za svaki od N proizvoda.
 *
 * Flow na Add Bundle (zajednički za oba režima):
 *   1. Sastav cart-a → Cloudflare Worker za discount code + savings metadata
 *   2. /cart/add.js sa pojedinačnim variantId-jevima (knjigovodstvo)
 *   3. /cart/update.js sa _bundle_* attributes (drawer čita)
 *   4. Refresh + open cart drawer (NEMA window.location.href dok user ne klikne
 *      checkout u drawer-u)
 */
const WORKER_URL =
  window.GEEZER_BUNDLE_WORKER_URL ||
  'https://geezer-bundle-discount.geezer.workers.dev';

(() => {
  const root = document.querySelector('.bundle-pdp');
  if (!root) return;

  const configEl = root.querySelector('[data-bundle-config]');
  const config = configEl ? JSON.parse(configEl.textContent) : null;
  if (!config) return;

  const addBtn = root.querySelector('[data-bundle-add]');
  const btnText = root.querySelector('[data-bundle-btn-text]');
  const errorEl = root.querySelector('[data-bundle-error]');
  const totalEl = root.querySelector('[data-bundle-total]');
  const wasEl = root.querySelector('.bundle-pdp__price-was');
  const saveEl = root.querySelector('.bundle-pdp__price-save');

  const mode = config.mode || (config.products ? 'legacy' : null);
  if (!mode) return;

  // ─────────────────────────────────────────────────────────────
  // STATE per mode
  // ─────────────────────────────────────────────────────────────

  /** Slot mode: po slot-u {currentProductId, optionValues: {optName: val}} */
  let slotStates = [];

  /** Legacy mode: po proizvodu mapa { optionName: selectedValue } */
  const selections = new Map();

  if (mode === 'slots') {
    if (!config.slots?.length) return;
    slotStates = config.slots.map((slot) => ({
      currentProductId: slot.defaultProductId,
      optionValues: {},
    }));
  } else {
    if (!config.products?.length) return;
    config.products.forEach((p) => selections.set(p.id, {}));
  }

  // ─────────────────────────────────────────────────────────────
  // VARIANT DERIVATION
  // ─────────────────────────────────────────────────────────────

  function getCurrentProductInSlot(slotIndex) {
    const slot = config.slots[slotIndex];
    const state = slotStates[slotIndex];
    return slot.products.find((p) => p.id === state.currentProductId);
  }

  function deriveVariantIdForSlot(slotIndex) {
    const product = getCurrentProductInSlot(slotIndex);
    if (!product) return null;
    const state = slotStates[slotIndex];
    const optionValues = product.options.map((opt) => state.optionValues[opt]);
    if (optionValues.some((v) => !v)) return null;
    const match = product.variants.find((v) =>
      v.options.every((val, i) => val === optionValues[i]),
    );
    return match?.available ? match.id : null;
  }

  function deriveVariantIdLegacy(product) {
    const sel = selections.get(product.id);
    const optionValues = product.options.map((opt) => sel[opt]);
    if (optionValues.some((v) => !v)) return null;
    const match = product.variants.find((v) =>
      v.options.every((val, i) => val === optionValues[i]),
    );
    return match?.available ? match.id : null;
  }

  // ─────────────────────────────────────────────────────────────
  // RECALC (total + button state)
  // ─────────────────────────────────────────────────────────────

  function recalc() {
    let allSelected = true;
    let total = 0;
    let unavailable = false;
    const slotPrices = []; // za scope='cheapest'/'last_slot' calc

    if (mode === 'slots') {
      for (let i = 0; i < config.slots.length; i++) {
        const variantId = deriveVariantIdForSlot(i);
        const product = getCurrentProductInSlot(i);
        if (!variantId) {
          allSelected = false;
          const allFilled = product?.options.every(
            (o) => slotStates[i].optionValues[o],
          );
          if (allFilled) unavailable = true;
          slotPrices.push(0);
          continue;
        }
        const variant = product.variants.find((v) => v.id === variantId);
        total += variant.price;
        slotPrices.push(variant.price);
      }
    } else {
      for (const product of config.products) {
        const variantId = deriveVariantIdLegacy(product);
        if (!variantId) {
          allSelected = false;
          const sel = selections.get(product.id);
          const allFilled = product.options.every((o) => sel[o]);
          if (allFilled) unavailable = true;
          continue;
        }
        const variant = product.variants.find((v) => v.id === variantId);
        total += variant.price;
      }
    }

    if (allSelected) {
      // Re-renderuj sve tri linije iznad bundle-a (was, now, save) sa real
      // varijant cenama. Scope diktira na koji deo se discount aplicira:
      //   'all'       — pct % od total (R4/R5/R6: discount na sve linije)
      //   'last_slot' — pct % od cene POSLEDNJEG slota (R3: discount na treći)
      //   'cheapest'  — pct % od NAJJEFTINIJE linije (R1/R2: tier escalation)
      const scope = config.discountScope || 'all';
      let discountBase = total;
      if (scope === 'last_slot' && slotPrices.length > 0) {
        discountBase = slotPrices[slotPrices.length - 1];
      } else if (scope === 'cheapest' && slotPrices.length > 0) {
        discountBase = Math.min(...slotPrices.filter((p) => p > 0));
      }
      const savings = Math.round((discountBase * config.discountPct) / 100);
      const discounted = total - savings;
      if (totalEl) totalEl.textContent = formatMoney(discounted);
      if (wasEl) wasEl.textContent = formatMoney(total);
      if (saveEl) {
        saveEl.textContent = `Uštedi ${formatMoney(savings)} (${config.discountPct}%)`;
      }
    }

    addBtn.disabled = !allSelected;
    if (unavailable) {
      btnText.textContent = 'Kombinacija nedostupna';
    } else if (!allSelected) {
      btnText.textContent = 'Izaberi sve varijante';
    } else {
      btnText.textContent = labelForButton();
    }
  }

  function labelForButton() {
    return config.discountPct > 0
      ? `Dodaj bundle u cart — ${config.discountPct}% OFF`
      : 'Dodaj bundle u cart';
  }

  // ─────────────────────────────────────────────────────────────
  // SLOT MODE: REPLACE PRODUCT + RE-RENDER UI
  // ─────────────────────────────────────────────────────────────

  /**
   * Menja proizvod u slot-u na novi (iz alternative-a). Re-renderuje
   * thumbnail, title, price, i variant select dropdownove (jer različiti
   * proizvodi mogu imati različite opcije, npr. Color+Size vs samo Size).
   */
  function replaceProductInSlot(slotIndex, newProductId) {
    const slot = config.slots[slotIndex];
    const newProduct = slot.products.find((p) => p.id === newProductId);
    if (!newProduct) return;

    slotStates[slotIndex] = {
      currentProductId: newProductId,
      optionValues: {},
    };

    const slotEl = root.querySelector(
      `[data-bundle-slot-index="${slotIndex}"]`,
    );
    if (!slotEl) return;

    // Thumb
    const thumb = slotEl.querySelector('[data-bundle-slot-thumb]');
    if (thumb && newProduct.featuredImage) {
      thumb.removeAttribute('srcset');
      thumb.removeAttribute('sizes');
      thumb.src = newProduct.featuredImage;
      thumb.alt = newProduct.title;
    }

    // Title
    const titleEl = slotEl.querySelector('[data-bundle-slot-title]');
    if (titleEl) titleEl.textContent = newProduct.title;

    // Price
    const priceEl = slotEl.querySelector('[data-bundle-slot-price]');
    if (priceEl) priceEl.textContent = formatMoney(newProduct.price);

    // Re-render option selects
    const optionsEl = slotEl.querySelector('[data-bundle-slot-options]');
    if (optionsEl) {
      optionsEl.innerHTML = newProduct.options
        .map(
          (optName) => `
        <select
          class="bundle-pdp__option-select"
          data-bundle-option
          data-slot-index="${slotIndex}"
          data-option-name="${escapeAttr(optName)}"
        >
          <option value="">Izaberi ${translateOptionLabel(optName)}</option>
          ${getOptionValuesForProduct(newProduct, optName)
            .map(
              (val) =>
                `<option value="${escapeAttr(val)}">${escapeHtml(val)}</option>`,
            )
            .join('')}
        </select>
      `,
        )
        .join('');
      // Re-attach listeners na nove select-e
      optionsEl.querySelectorAll('[data-bundle-option]').forEach(wireSlotOption);
    }

    // Zatvori replace dropdown
    const replaceList = slotEl.querySelector('[data-bundle-slot-replace-list]');
    const replaceBtn = slotEl.querySelector('[data-bundle-slot-replace-toggle]');
    if (replaceList) replaceList.hidden = true;
    if (replaceBtn) replaceBtn.setAttribute('aria-expanded', 'false');

    recalc();
  }

  function getOptionValuesForProduct(product, optionName) {
    const optIndex = product.options.indexOf(optionName);
    if (optIndex === -1) return [];
    const values = new Set();
    for (const v of product.variants) {
      if (v.options[optIndex]) values.add(v.options[optIndex]);
    }
    return Array.from(values);
  }

  /**
   * Display translation za option name placeholder.
   * "Color" → "boju", "Size" → "veličinu" (srpski akuzativ jer "izaberi"
   * traži akuzativ). Backend option ime ostaje engleski (variant matching).
   * Mora biti sinhronizovano sa Liquid case statement-om u section fajlu.
   */
  function translateOptionLabel(optName) {
    const lower = String(optName).toLowerCase();
    if (lower === 'color') return 'boju';
    if (lower === 'size') return 'veličinu';
    return lower;
  }

  function escapeAttr(s) {
    return String(s).replace(/"/g, '&quot;').replace(/&/g, '&amp;');
  }
  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // ─────────────────────────────────────────────────────────────
  // SLOT MODE: OPTION SELECT HANDLER
  // ─────────────────────────────────────────────────────────────

  function wireSlotOption(select) {
    select.addEventListener('change', (e) => {
      const slotIndex = parseInt(e.target.dataset.slotIndex, 10);
      const optionName = e.target.dataset.optionName;
      slotStates[slotIndex].optionValues[optionName] = e.target.value;
      updateSlotThumb(slotIndex);
      recalc();
    });
  }

  function updateSlotThumb(slotIndex) {
    const product = getCurrentProductInSlot(slotIndex);
    if (!product) return;
    const sel = slotStates[slotIndex].optionValues;
    const partialMatch = product.variants.find((v) =>
      product.options.every((optName, i) => {
        const selectedVal = sel[optName];
        return !selectedVal || v.options[i] === selectedVal;
      }),
    );
    if (!partialMatch?.image) return;
    const slotEl = root.querySelector(
      `[data-bundle-slot-index="${slotIndex}"]`,
    );
    const img = slotEl?.querySelector('[data-bundle-slot-thumb]');
    if (img) {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = partialMatch.image;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // LEGACY MODE: OPTION SELECT HANDLER + THUMB UPDATE
  // ─────────────────────────────────────────────────────────────

  function wireLegacyOption(select) {
    select.addEventListener('change', (e) => {
      const productId = parseInt(e.target.dataset.productId, 10);
      const optionName = e.target.dataset.optionName;
      const productSel = selections.get(productId);
      productSel[optionName] = e.target.value;
      updateLegacyThumb(productId);
      recalc();
    });
  }

  function updateLegacyThumb(productId) {
    const product = config.products.find((p) => p.id === productId);
    if (!product) return;
    const sel = selections.get(productId);
    const partialMatch = product.variants.find((v) =>
      product.options.every((optName, i) => {
        const selectedVal = sel[optName];
        return !selectedVal || v.options[i] === selectedVal;
      }),
    );
    if (!partialMatch?.image) return;
    const card = root.querySelector(`[data-bundle-product-id="${productId}"]`);
    const img = card?.querySelector('.bundle-pdp__item-thumb');
    if (img) {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = partialMatch.image;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // ADD BUNDLE TO CART (zajednički za oba režima)
  // ─────────────────────────────────────────────────────────────

  async function addBundleToCart() {
    const items = [];
    const cartItemsForWorker = [];
    let totalCents = 0;

    if (mode === 'slots') {
      for (let i = 0; i < config.slots.length; i++) {
        const variantId = deriveVariantIdForSlot(i);
        if (!variantId) {
          showError('Nedostaju izbori — proveri sve varijante.');
          return;
        }
        const product = getCurrentProductInSlot(i);
        const variant = product.variants.find((v) => v.id === variantId);
        items.push({ id: variantId, quantity: 1 });
        cartItemsForWorker.push({
          variantId,
          productId: product.id,
          quantity: 1,
          price: variant.price,
          collectionIds: product.collectionIds || [],
        });
        totalCents += variant.price;
      }
    } else {
      for (const product of config.products) {
        const variantId = deriveVariantIdLegacy(product);
        if (!variantId) {
          showError('Nedostaju izbori — proveri sve varijante.');
          return;
        }
        const variant = product.variants.find((v) => v.id === variantId);
        items.push({ id: variantId, quantity: 1 });
        cartItemsForWorker.push({
          variantId,
          productId: product.id,
          quantity: 1,
          price: variant.price,
          collectionIds: product.collectionIds || [],
        });
        totalCents += variant.price;
      }
    }

    addBtn.disabled = true;
    btnText.textContent = 'Računam popust...';

    let workerJson = null;
    try {
      // forceRule iz config.bundleRule (custom.bundle_rule metafield) — ako
      // PDP eksplicitno deklariše svoje pravilo, worker honoriše to umesto
      // da bira best-of-all. Tako message + savings konzistentni sa PDP-om.
      // Trim defensively — admin metafield može imati trailing whitespace
      // ili tab karakter zbog copy-paste/autocomplete bug-a. "R3\t" === "R3"
      // posle trim().
      const workerPayload = { cartItems: cartItemsForWorker };
      const trimmedRule = config.bundleRule && config.bundleRule.trim();
      if (trimmedRule) {
        workerPayload.forceRule = trimmedRule;
      }
      const workerRes = await fetch(WORKER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(workerPayload),
      });
      if (workerRes.ok) {
        workerJson = await workerRes.json();
        if (!workerJson.code) {
          console.info('[bundle-cart] no rule matched:', workerJson.reason);
        }
      } else {
        console.warn('[bundle-cart] Worker', workerRes.status, '— bez popusta');
      }
    } catch (err) {
      console.warn('[bundle-cart] Worker fail — bez popusta:', err);
    }

    const discountCode = workerJson?.code || null;
    const discountPct = workerJson?.percentage || 0;
    const discountMessage = workerJson?.message || '';

    // Worker vraća stvarno izračunatu uštedu (sum of pct × each target line).
    // Preferiramo to nad lokalnom procenom jer worker tačno zna koji su
    // variantId-jevi targetovani u Shopify discount code-u — drawer
    // prikaz onda match-uje 1:1 sa onim što checkout aplicira.
    let savingsCents = workerJson?.savingsCents;
    if (savingsCents === undefined || savingsCents === null) {
      // Fallback ako worker nije vratio savings (stara verzija): lokalna
      // procena prema scope-u.
      let discountBaseCents = totalCents;
      const scope = config.discountScope || 'all';
      if (scope === 'last_slot' && cartItemsForWorker.length > 0) {
        discountBaseCents =
          cartItemsForWorker[cartItemsForWorker.length - 1].price;
      } else if (scope === 'cheapest' && cartItemsForWorker.length > 0) {
        discountBaseCents = Math.min(...cartItemsForWorker.map((i) => i.price));
      }
      savingsCents =
        discountCode && discountPct > 0
          ? Math.round((discountBaseCents * discountPct) / 100)
          : 0;
    }

    btnText.textContent = 'Dodajem u cart...';
    console.log('[bundle-cart] Items:', items);
    console.log('[bundle-cart] Worker:', workerJson);

    try {
      // INTERIM RESTRICTION (Path B / Worker mode): blokiramo dodavanje
      // drugog bundle-a ako postoji. Razlog: Shopify session drži samo
      // 1 manual discount code; više bundle-ova bi vodilo u confusing UX
      // gde poslednji prepisuje prethodni. Migrira se kad pređemo na
      // Path A (App Store Function) — Function pristup dozvoljava više
      // simultanih popusta pa će ova blokada biti nepotrebna.
      try {
        const cartCheckRes = await fetch('/cart.js');
        if (cartCheckRes.ok) {
          const cartCheck = await cartCheckRes.json();
          if (cartCheck.attributes?._bundle_code) {
            console.log('[bundle-cart] Bundle već postoji u cart-u — block');
            showError(
              'Bundle je već u korpi. Idi na checkout ili obriši trenutni bundle iz korpe da bi dodao novi.',
            );
            addBtn.disabled = false;
            btnText.textContent = labelForButton();
            return;
          }
        }
      } catch (err) {
        console.warn('[bundle-cart] cart check fail (non-fatal):', err);
      }

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!addRes.ok) {
        const err = await addRes.json().catch(() => ({}));
        throw new Error(err.description || `Greška ${addRes.status}`);
      }

      if (discountCode) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              _bundle_code: discountCode,
              _bundle_message: discountMessage,
              _bundle_savings: String(savingsCents),
              _bundle_pct: String(discountPct),
              _bundle_variant_ids: items.map((i) => i.id).join(','),
            },
          }),
        });
      } else {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              _bundle_code: '',
              _bundle_message: '',
              _bundle_savings: '',
              _bundle_pct: '',
              _bundle_variant_ids: '',
            },
          }),
        });
      }

      await refreshAndOpenDrawer();
      addBtn.disabled = false;
      btnText.textContent = labelForButton();
    } catch (err) {
      console.error('[bundle-cart] add failed:', err);
      showError(err.message || 'Dodavanje nije uspelo. Pokušaj ponovo.');
      addBtn.disabled = false;
      btnText.textContent = labelForButton();
    }
  }

  async function refreshAndOpenDrawer() {
    try {
      // Dovuci i drawer i header sekciju u JEDAN round-trip — header sadrži
      // cart-icon-bubble koji prikazuje broj stavki u cart-u. Bez njega
      // ostaje stale (0 ili stara vrednost) jer Rise pub/sub ne fire-uje
      // kad mi raw fetch-ujemo /cart/add.js.
      const res = await fetch('/?sections=cart-drawer,header');
      if (res.ok) {
        const data = await res.json();

        // Update drawer
        const drawerHtml = data['cart-drawer'];
        if (drawerHtml) {
          const newDoc = new DOMParser().parseFromString(drawerHtml, 'text/html');
          const newDrawer = newDoc.querySelector('cart-drawer');
          const oldDrawer = document.querySelector('cart-drawer');
          if (newDrawer && oldDrawer) {
            oldDrawer.innerHTML = newDrawer.innerHTML;
            oldDrawer.classList.remove('is-empty');
          }
        }

        // Update cart icon bubble (zive u header sekciji)
        const headerHtml = data['header'];
        if (headerHtml) {
          const newDoc = new DOMParser().parseFromString(headerHtml, 'text/html');
          const newBubble = newDoc.querySelector('#cart-icon-bubble');
          const oldBubble = document.getElementById('cart-icon-bubble');
          if (newBubble && oldBubble) {
            oldBubble.innerHTML = newBubble.innerHTML;
          }
        }
      }
    } catch (err) {
      console.warn('[bundle-cart] drawer/header refresh fail:', err);
    }

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    if (typeof drawer.open === 'function') {
      drawer.open();
    } else {
      drawer.classList.add('active', 'animate');
      document.body.classList.add('overflow-hidden');
    }
  }

  function showError(msg) {
    if (!errorEl) {
      alert(msg);
      return;
    }
    errorEl.textContent = msg;
    errorEl.hidden = false;
    setTimeout(() => {
      errorEl.hidden = true;
    }, 5000);
  }

  function formatMoney(cents) {
    const amount = (cents / 100).toLocaleString('sr-RS', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${amount} RSD`;
  }

  // ─────────────────────────────────────────────────────────────
  // WIRE EVENT LISTENERS
  // ─────────────────────────────────────────────────────────────

  if (mode === 'slots') {
    // Initial option select-i (rendered by Liquid za default produkt po slotu)
    root.querySelectorAll('[data-bundle-option]').forEach(wireSlotOption);

    // Replace toggle (Zameni proizvod ▾)
    root.querySelectorAll('[data-bundle-slot-replace-toggle]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const slotEl = btn.closest('[data-bundle-slot-index]');
        const list = slotEl.querySelector('[data-bundle-slot-replace-list]');
        if (!list) return;
        const isOpen = !list.hidden;
        list.hidden = isOpen;
        btn.setAttribute('aria-expanded', String(!isOpen));
      });
    });

    // Replace item klik → swap product u slotu
    root.querySelectorAll('[data-bundle-slot-replace-item]').forEach((item) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        const slotEl = item.closest('[data-bundle-slot-index]');
        const slotIndex = parseInt(slotEl.dataset.bundleSlotIndex, 10);
        const newProductId = parseInt(item.dataset.productId, 10);
        replaceProductInSlot(slotIndex, newProductId);
      });
    });

    // Klik van replace dropdown-a ga zatvara
    document.addEventListener('click', (e) => {
      root.querySelectorAll('[data-bundle-slot-replace-list]').forEach((list) => {
        const slotEl = list.closest('[data-bundle-slot-index]');
        if (slotEl && !slotEl.contains(e.target) && !list.hidden) {
          list.hidden = true;
          const btn = slotEl.querySelector('[data-bundle-slot-replace-toggle]');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        }
      });
    });
  } else {
    root.querySelectorAll('[data-bundle-option]').forEach(wireLegacyOption);
  }

  addBtn.addEventListener('click', (e) => {
    e.preventDefault();
    addBundleToCart();
  });

  recalc();
})();
